import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import YaslanLogo from './YaslanLogo'
import { LINKS, LEGAL } from '../lib/links'
import { openCookiePreferences } from '../lib/cookieConsent'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

type FooterLink = { label: string; href: string }
type FooterColumn = { title: string; links: FooterLink[] }

// Only real, populated profiles belong here — an icon linking to a platform's
// home page reads as broken. YouTube was dropped for that reason (2026-08-03);
// re-add it here and in LINKS once a channel exists.
const SOCIALS = [
  { label: 'LinkedIn', href: LINKS.linkedin, file: '/social/linkedin.svg' },
  { label: 'Instagram', href: LINKS.instagram, file: '/social/instagram.svg' },
  { label: 'Facebook', href: LINKS.facebook, file: '/social/facebook.svg' },
]

export default function Footer() {
  const { t } = useT()
  const locale = useLocale()

  const columns: FooterColumn[] = [
    {
      title: t('footer.colProduct'),
      links: [
        { label: t('footer.linkSectors'), href: localizedPath('sectors', locale) },
        { label: t('footer.linkFulfillment'), href: localizedPath('fulfillment', locale) },
        { label: t('footer.linkStorage'), href: localizedPath('service.storage', locale) },
        { label: t('footer.linkPickpack'), href: localizedPath('service.pickpack', locale) },
        { label: t('footer.linkShipping'), href: localizedPath('service.shipping', locale) },
        { label: t('footer.linkReturns'), href: localizedPath('service.returns', locale) },
        { label: t('footer.linkPricing'), href: localizedPath('pricing', locale) },
        { label: t('footer.linkCarriers'), href: '/#carriers' },
        { label: t('footer.linkAdr'), href: localizedPath('expertise.adr', locale) },
        { label: t('footer.linkAfsca'), href: localizedPath('expertise.afsca', locale) },
      ],
    },
    {
      title: t('footer.colCompany'),
      links: [
        { label: t('footer.linkAbout'), href: localizedPath('about', locale) },
        { label: t('footer.linkBlog'), href: localizedPath('blog', locale) },
        { label: t('footer.linkContact'), href: localizedPath('contact', locale) },
      ],
    },
    {
      title: t('footer.colResources'),
      links: [
        { label: t('footer.linkFaq'), href: '/#faq' },
        { label: t('footer.linkIntegrations'), href: '/#integrations' },
        { label: t('footer.linkTestimonials'), href: '/#stats' },
      ],
    },
    {
      title: t('footer.colLegal'),
      links: [
        { label: t('footer.linkLegalNotice'), href: localizedPath('legal', locale) },
        { label: t('footer.linkTerms'), href: localizedPath('terms', locale) },
        { label: t('footer.linkPrivacy'), href: localizedPath('privacy', locale) },
        { label: t('footer.linkCookies'), href: localizedPath('cookies', locale) },
      ],
    },
  ]

  return (
    <footer className="relative border-t border-ink-100 bg-white">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3">
              <YaslanLogo variant="black" height={28} />
            </div>
            <p className="mt-4 text-sm text-ink-600 leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="mt-6 flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('footer.socialAria', { name: s.label })}
                  className="group w-9 h-9 rounded-full border border-ink-200 hover:border-accent-300 hover:bg-accent-50 flex items-center justify-center transition-colors"
                >
                  <img
                    src={s.file}
                    alt={`Logo ${s.label}`}
                    draggable={false}
                    className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity"
                  />
                </a>
              ))}
            </div>
          </motion.div>

          {columns.map((col, i) => (
            <motion.div
              key={col.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
            >
              <h3 className="text-sm font-semibold text-ink-950">{col.title}</h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => {
                  const isInternal = l.href.startsWith('/') && !l.href.includes('#')
                  return (
                    <li key={l.label}>
                      {isInternal ? (
                        <Link
                          to={l.href}
                          className="text-sm text-ink-600 hover:text-ink-950 transition-colors"
                        >
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          className="text-sm text-ink-600 hover:text-ink-950 transition-colors"
                        >
                          {l.label}
                        </a>
                      )}
                    </li>
                  )
                })}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 pt-6 border-t border-ink-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-ink-500">
          <p>
            © 2026 {LEGAL.company} ({LEGAL.legalForm}) — TVA {LEGAL.vat}.{' '}
            <button
              type="button"
              onClick={openCookiePreferences}
              className="text-ink-500 hover:text-ink-950 underline underline-offset-2 transition-colors"
            >
              {t('footer.manageCookies')}
            </button>
          </p>
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {t('footer.systemsOperational')}
            </span>
          </div>
        </div>
      </div>
    </footer>
  )
}
